/**
 * Global audit-trail configuration (client side) — the single source of truth
 * that drives, per form, whether the audit log is shown and whether a reason is
 * prompted on edit/delete (CLAUDE.md standing rule 11).  Ported from ALPACA's
 * Settings → Audit Trail Configuration (AuditTrailToggleControl).
 *
 * The map is fetched once after login from `GET /api/v1/app-config`
 * (`auditTrailConfig`) and pushed in via `setAuditTrailConfig` (AppShell), and
 * re-pushed when the admin saves the Settings → Audit Trail Configuration grid.
 * Consumers read it through the helpers below — never hardcode a per-form audit
 * decision again.
 *
 * Defaults when a form is NOT in the map (matches the API/reader): audit ON,
 * reason OFF, retention "1Y".  So nothing disappears until an admin explicitly
 * turns a form's audit OFF.
 */
import { useSyncExternalStore } from 'react';

export interface AuditFormConfig {
  formName: string;
  auditEnabled: boolean;
  reasonRequired: boolean;
  retentionDays: string;
}

/** formName (lower-cased) → config. */
let _config: Record<string, AuditFormConfig> = {};
const listeners = new Set<() => void>();
const norm = (formName: string) => (formName ?? '').trim().toLowerCase();

/** Replace the whole config map (called by AppShell after login + by the
 *  Settings page after a save).  Keys are normalised so look-ups are
 *  case-insensitive. */
export function setAuditTrailConfig(
  map: Record<string, AuditFormConfig> | null | undefined,
): void {
  const next: Record<string, AuditFormConfig> = {};
  if (map) {
    for (const k of Object.keys(map)) {
      const v = map[k];
      if (!v) continue;
      const formName = v.formName ?? k;
      next[norm(formName)] = {
        formName,
        auditEnabled: v.auditEnabled !== false,        // default ON
        reasonRequired: v.reasonRequired === true,      // default OFF
        retentionDays: v.retentionDays || '1Y',
      };
    }
  }
  _config = next;
  listeners.forEach((l) => l());
}

/** True unless an admin has explicitly turned this form's audit OFF. */
export function isAuditEnabled(formName: string): boolean {
  const c = _config[norm(formName)];
  return c ? c.auditEnabled : true;
}

/** True only when an admin has turned ReasonRequired ON for this form. */
export function isReasonRequired(formName: string): boolean {
  const c = _config[norm(formName)];
  return c ? c.reasonRequired : false;
}

/** Display-only retention (not enforced) — "1Y" when unset. */
export function getAuditRetentionDays(formName: string): string {
  const c = _config[norm(formName)];
  return c ? c.retentionDays : '1Y';
}

/** Snapshot of the whole map (used by the Settings config page to overlay onto
 *  its form catalog). */
export function getAuditTrailConfig(): Record<string, AuditFormConfig> {
  return _config;
}

// ── React binding ─────────────────────────────────────────────────────────
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Re-renders a component when the audit config changes.  Returns the live map. */
export function useAuditTrailConfig(): Record<string, AuditFormConfig> {
  return useSyncExternalStore(subscribe, getAuditTrailConfig, getAuditTrailConfig);
}
